import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from '../../../utils/toast';
import {
  Key, KeyRound, ShieldCheck, ShieldAlert, Copy, Plus, AlertTriangle,
  CheckCircle, XCircle, RefreshCw, X, Activity, Server,
  Power, Clock, Database, Pencil,
  Layers, Zap, Trash2, ChevronDown, Info, Lock,
} from 'lucide-react';

import { api, getPublicApiOrigin } from '../../../services/api';
import { copyToClipboard } from '../../../utils/clipboard';
import AccordionSection from '../../../components/UI/AccordionSection';
import Portal from '../../../components/UI/Portal';
import ConfirmModal from '../../../components/UI/ConfirmModal';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';
import { FilterButton } from '../../../components/UI/FilterButton';
import DatePickerField from '../../../components/UI/DatePickerField';
import StatusBadge from '../../../components/UI/StatusBadge';
import { formatDate, formatDateTime } from '../../../utils/dateUtils';
import { useConfigurableServiceApiKeys } from '../../../hooks/useApiOperationsData';

// Icon + soft bg used in accordion headers (StatusBadge handles the chip).
const STATUS_ICON = {
  active:        { icon: ShieldCheck, bg: 'bg-green-50',  text: 'text-green-700' },
  expiring_soon: { icon: Clock,       bg: 'bg-amber-50',  text: 'text-amber-700' },
  expired:       { icon: AlertTriangle, bg: 'bg-gray-50', text: 'text-gray-600' },
  disabled:      { icon: Power,       bg: 'bg-orange-50', text: 'text-orange-700' },
  revoked:       { icon: ShieldAlert, bg: 'bg-red-50',    text: 'text-red-700' },
};

const RISK_CONFIG = {
  low:    { label: 'Low risk',    bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  medium: { label: 'Medium risk', bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200' },
  high:   { label: 'High risk',   bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200' },
};

// ---------------------------------------------------------------------------
// data_access string → structured field chips
//
// Backend stores `data_access` as a sentence-form prose (so it reads naturally
// in audit exports / DPO reviews). For the picker UI we want individual chips,
// one per field, with any parenthetical qualifier as a tooltip.
//
// splitDataAccess() splits on commas that are NOT inside parentheses so a
// qualifier like "leave_type (e.g. AL, OIL, MC — NOT the underlying medical
// reason)" stays as a single field with the parenthetical preserved.
// ---------------------------------------------------------------------------
const splitDataAccess = (str) => {
  if (!str) return [];
  const out = [];
  let buf = '';
  let depth = 0;
  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    if (ch === ',' && depth === 0) {
      const t = buf.trim();
      if (t) out.push(parseField(t));
      buf = '';
    } else {
      buf += ch;
    }
  }
  const tail = buf.trim().replace(/\.$/, '');
  if (tail) out.push(parseField(tail));
  return out;
};

const parseField = (raw) => {
  const m = raw.match(/^([^(]+?)\s*(?:\(([^)]*)\))?\s*$/);
  if (!m) return { name: raw, qualifier: null };
  return { name: m[1].trim(), qualifier: (m[2] || '').trim() || null };
};

// ---------------------------------------------------------------------------
// EXCLUDED_DOMAINS — UI-side mirror of the EXCLUDED list codified in
// staff_core/service_auth.py SERVICE_SCOPE_REGISTRY docstring.
//
// Shown in the "Looking for NRIC, DOB, salary…?" panel below the Resources
// picker.  The two lists MUST stay in sync; if the backend docstring adds a
// domain, mirror it here.
// ---------------------------------------------------------------------------
const EXCLUDED_DOMAINS = [
  {
    id: 'identity-documents',
    label: 'Identity documents',
    examples: ['NRIC / FIN (raw)', 'Passport', 'Driving licence', 'Work permit / S-pass / EP imagery', 'OCR-extracted document fields'],
    reason: 'High-blast-radius PII. A single leak triggers an MOM/PDPC reportable incident.',
  },
  {
    id: 'health-data',
    label: 'Health & medical data',
    examples: ['MC contents', 'Confidential leave details', 'Maternity / childcare medical evidence'],
    reason: 'Statutorily confidential under PDPA Special Category Personal Data rules.',
  },
  {
    id: 'family-dependents',
    label: 'Family, dependents, emergency contacts',
    examples: ['Next-of-kin name & phone', 'Dependent name / DOB', 'Home address'],
    reason: 'Third-party personal data — we have no consent to redistribute beyond statutory uses.',
  },
  {
    id: 'financial-banking',
    label: 'Financial & banking',
    examples: ['Bank account number', 'Allowance ledger', 'Salary history', 'Termination payment'],
    reason: 'Direct deposit fraud surface; statutorily confidential.',
  },
  {
    id: 'payroll-outputs',
    label: 'Payroll outputs',
    examples: ['Payslip', 'Payroll run', 'CPF / SDL deductions', 'Payroll flags'],
    reason: 'MOM-confidential by statute. Never leaves the HR Portal via a long-lived key.',
  },
  {
    id: 'tax-statutory',
    label: 'Tax & statutory filings',
    examples: ['Year-to-date summary', 'IR8A / IR21', 'Monthly BIK record', 'Tax relief claims'],
    reason: 'IRAS-confidential; redistribution is a tax-act offence.',
  },
  {
    id: 'salary-letters',
    label: 'Salary-bearing letters',
    examples: ['Employment confirmation letter', 'Salary letter', 'Asset acknowledgement letter (deduction terms)'],
    reason: 'Letters routinely quote salary; treating the document as "non-payroll" leaks salary by transitivity.',
  },
  {
    id: 'psychometric',
    label: 'Psychometric & assessment',
    examples: ['Big Five personality results', 'Individual assessment scores'],
    reason: 'Special Category Personal Data; consent is purpose-bound to the assessment context.',
  },
  {
    id: 'pre-onboarding',
    label: 'Pre-onboarding (salary intent)',
    examples: ['Pre-onboarding application', 'Intended allowance / offer terms', 'Pre-onboarding workflow'],
    reason: 'Contains salary intent before contract. Hiring ATS candidates are a separate HR Lifecycle scope.',
  },
  {
    id: 'compliance-dossiers',
    label: 'Compliance dossiers',
    examples: ['LQS compliance record', 'EP snapshot', 'Payment deadline tracking'],
    reason: 'Cross-references all of the above — exposing the dossier exposes everything inside it.',
  },
];

// Build a quick lookup of excluded-keyword → domain id for the empty-state hint
// in the search box.  Keys are lowercase, single-word fragments.
const EXCLUDED_KEYWORD_INDEX = (() => {
  const map = new Map();
  const add = (kw, domainId) => map.set(kw.toLowerCase(), domainId);
  add('nric', 'identity-documents');
  add('fin', 'identity-documents');
  add('passport', 'identity-documents');
  add('licence', 'identity-documents');
  add('license', 'identity-documents');
  add('permit', 'identity-documents');
  add('work permit', 'identity-documents');
  add('s-pass', 'identity-documents');
  add('mc', 'health-data');
  add('medical', 'health-data');
  add('childcare', 'health-data');
  add('maternity', 'health-data');
  add('dob', 'family-dependents');
  add('birth', 'family-dependents');
  add('birthday', 'family-dependents');
  add('age', 'family-dependents');
  add('nok', 'family-dependents');
  add('next of kin', 'family-dependents');
  add('emergency', 'family-dependents');
  add('dependent', 'family-dependents');
  add('address', 'family-dependents');
  add('home address', 'family-dependents');
  add('bank', 'financial-banking');
  add('account', 'financial-banking');
  add('iban', 'financial-banking');
  add('allowance', 'financial-banking');
  add('salary', 'financial-banking');
  add('basic pay', 'financial-banking');
  add('pay', 'payroll-outputs');
  add('payslip', 'payroll-outputs');
  add('payroll', 'payroll-outputs');
  add('cpf', 'payroll-outputs');
  add('sdl', 'payroll-outputs');
  add('deduction', 'payroll-outputs');
  add('tax', 'tax-statutory');
  add('iras', 'tax-statutory');
  add('ir8a', 'tax-statutory');
  add('ir21', 'tax-statutory');
  add('ytd', 'tax-statutory');
  add('bik', 'tax-statutory');
  add('letter', 'salary-letters');
  add('coe', 'salary-letters');
  add('big five', 'psychometric');
  add('big-five', 'psychometric');
  add('personality', 'psychometric');
  add('assessment', 'psychometric');
  add('pre-onboarding', 'pre-onboarding');
  add('intended allowance', 'pre-onboarding');
  add('offer terms', 'pre-onboarding');
  add('lqs', 'compliance-dossiers');
  return map;
})();

const findExcludedHit = (query) => {
  if (!query) return null;
  const q = query.trim().toLowerCase();
  if (!q) return null;
  if (EXCLUDED_KEYWORD_INDEX.has(q)) {
    const id = EXCLUDED_KEYWORD_INDEX.get(q);
    return EXCLUDED_DOMAINS.find(d => d.id === id) || null;
  }
  // Substring fallback so "nric/fin" or "salary letter" still resolves.
  for (const [kw, id] of EXCLUDED_KEYWORD_INDEX.entries()) {
    if (q.includes(kw) || kw.includes(q)) {
      return EXCLUDED_DOMAINS.find(d => d.id === id) || null;
    }
  }
  return null;
};

const scopeMatchesQuery = (scope, query) => {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    scope.label,
    scope.id,
    scope.description,
    scope.category,
    scope.data_access,
    ...(scope.compliance_tags || []),
    ...(scope.endpoints || []),
  ].filter(Boolean).join(' \u0001 ').toLowerCase();
  return haystack.includes(q);
};

const groupScopesByCategory = (scopes, categoryOrder = []) => {
  const groups = [];
  const indexByCat = new Map();

  const ensureGroup = (cat) => {
    if (!indexByCat.has(cat)) {
      indexByCat.set(cat, groups.length);
      groups.push({ category: cat, items: [] });
    }
  };

  (categoryOrder || []).forEach(ensureGroup);
  (scopes || []).forEach((scope) => {
    const cat = scope.category || 'Other';
    ensureGroup(cat);
    groups[indexByCat.get(cat)].items.push(scope);
  });

  return groups.filter((group) => group.items.length > 0);
};

const CategoryChips = ({ categories, value, onChange }) => {
  if (!categories?.length) return null;
  return (
    <div className="flex items-center flex-wrap gap-2">
      <button
        type="button"
        aria-pressed={!value}
        onClick={() => onChange('')}
        className={`tab-btn-admin tab-btn-admin-sm ${!value ? 'active' : ''}`}
      >
        All
      </button>
      {categories.map((cat) => (
        <button
          key={cat}
          type="button"
          aria-pressed={value === cat}
          onClick={() => onChange(value === cat ? '' : cat)}
          className={`tab-btn-admin tab-btn-admin-sm ${value === cat ? 'active' : ''}`}
        >
          {cat}
        </button>
      ))}
    </div>
  );
};

const STATUS_FILTER_OPTIONS = [
  { id: 'active',        label: 'Active' },
  { id: 'expiring_soon', label: 'Expiring Soon' },
  { id: 'disabled',      label: 'Disabled' },
  { id: 'revoked',       label: 'Revoked' },
  { id: 'expired',       label: 'Expired' },
];

const formatRelativeTime = (dateString) => {
  if (!dateString) return 'Never';
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
};

const getExpiryBarPercent = (key) => {
  if (!key.expires_at || ['revoked', 'expired'].includes(key.status)) return 0;
  const created = new Date(key.created_at);
  const expires = new Date(key.expires_at);
  const now = new Date();
  const total = expires - created;
  const elapsed = now - created;
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, ((total - elapsed) / total) * 100));
};

const getExpiryBarColor = (key) => {
  if (['revoked', 'expired'].includes(key.status)) return 'bg-red-500';
  if (key.status === 'disabled') return 'bg-orange-500';
  if (key.days_remaining == null) return 'bg-green-500';
  if (key.days_remaining <= 7) return 'bg-red-500';
  if (key.days_remaining <= 14) return 'bg-orange-500';
  if (key.days_remaining <= 30) return 'bg-amber-500';
  return 'bg-green-500';
};

// Default configuration targets the central HR-admin console. The asset-admin
// portal renders this same component with an asset-scoped `apiBase` and the
// HR-only "excluded PII domains" guidance suppressed.
const DEFAULT_CONFIG = {
  apiBase: '/service-api-keys',
  showExcludedInfo: true,
};

const ServiceApiKeysTab = ({
  config,
  initialSearch = '',
  openCreateSignal = false,
  onCreateSignalHandled,
  showToolbarCreate = true,
} = {}) => {
  const { apiBase, showExcludedInfo } = { ...DEFAULT_CONFIG, ...(config || {}) };
  const queryClient = useQueryClient();
  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useConfigurableServiceApiKeys(apiBase);

  const keys = useMemo(() => data?.keys || [], [data?.keys]);
  const scopes = data?.scopes || [];
  const categoryOrder = data?.categories || [];
  const summary = data?.summary || {};

  const [searchQuery, setSearchQuery] = useState(initialSearch || '');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState({});

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createPrefill, setCreatePrefill] = useState({ scopes: [], search: '' });
  const [revealedKey, setRevealedKey] = useState(null);
  const [keyToRevoke, setKeyToRevoke] = useState(null);
  const [keyToRotate, setKeyToRotate] = useState(null);
  const [keyToEdit, setKeyToEdit] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  useEffect(() => {
    if (initialSearch != null) setSearchQuery(initialSearch);
  }, [initialSearch]);

  useEffect(() => {
    if (openCreateSignal) {
      setCreatePrefill({ scopes: [], search: searchQuery || '' });
      setShowCreateModal(true);
      onCreateSignalHandled?.();
    }
  }, [openCreateSignal, onCreateSignalHandled, searchQuery]);

  const invalidateKeys = () => {
    queryClient.invalidateQueries({ queryKey: ['api-operations'] });
  };

  const toggleExpand = (id) => {
    setExpandedKeys(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleToggleActive = async (key) => {
    try {
      setActionBusy(true);
      const next = !key.is_active;
      await api.patch(`${apiBase}/${key.id}/`, { is_active: next });
      toast.success(next ? `${key.app_name} re-enabled` : `${key.app_name} disabled`);
      invalidateKeys();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update key');
    } finally {
      setActionBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (!keyToRevoke) return;
    try {
      setActionBusy(true);
      await api.delete(`${apiBase}/${keyToRevoke.id}/`);
      toast.success(`Key for ${keyToRevoke.app_name} revoked permanently`);
      invalidateKeys();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to revoke key');
    } finally {
      setActionBusy(false);
      setKeyToRevoke(null);
    }
  };

  const handleRotateConfirm = async () => {
    if (!keyToRotate) return;
    try {
      setActionBusy(true);
      const response = await api.post(`${apiBase}/${keyToRotate.id}/rotate/`);
      setRevealedKey({ ...response.data, mode: 'rotated' });
      invalidateKeys();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to rotate key');
    } finally {
      setActionBusy(false);
      setKeyToRotate(null);
    }
  };

  const filteredKeys = useMemo(() => {
    let out = keys;
    if (statusFilter.length > 0) {
      out = out.filter(k => statusFilter.includes(k.status));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      out = out.filter(k =>
        k.app_name?.toLowerCase().includes(q) ||
        k.description?.toLowerCase().includes(q) ||
        k.allowed_scopes?.some(s =>
          s.id.toLowerCase().includes(q) ||
          s.label.toLowerCase().includes(q),
        )
      );
    }
    return out;
  }, [keys, statusFilter, searchQuery]);

  const catalogMatchCount = useMemo(
    () => (scopes || []).filter((scope) => scopeMatchesQuery(scope, searchQuery)).length,
    [scopes, searchQuery],
  );

  const openCreateModal = (prefill = {}) => {
    setCreatePrefill({
      scopes: prefill.scopes || [],
      search: prefill.search || '',
    });
    setShowCreateModal(true);
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <XCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Couldn&apos;t load API keys</h3>
        <p className="text-sm text-gray-500 mb-4">Check your connection and try again.</p>
        <button type="button" onClick={() => refetch()} className="btn btn-admin-primary">
          <RefreshCw className="w-4 h-4" /> Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Metrics — 4-up uses full summary object */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
        <MetricCard
          icon={KeyRound}
          iconBg="bg-red-100"
          iconColor="text-red-600"
          value={summary.total_keys || 0}
          label="Total Keys"
        />
        <MetricCard
          icon={ShieldCheck}
          iconBg="bg-green-100"
          iconColor="text-green-600"
          value={summary.active_keys || 0}
          label="Active Keys"
          sub={summary.expiring_keys > 0
            ? `${summary.expiring_keys} expiring soon` : null}
        />
        <MetricCard
          icon={Activity}
          iconBg="bg-blue-100"
          iconColor="text-blue-600"
          value={(summary.requests_24h || 0).toLocaleString()}
          label="Requests (24h)"
        />
        <MetricCard
          icon={XCircle}
          iconBg="bg-red-100"
          iconColor="text-red-600"
          value={summary.errors_24h || 0}
          label="Errors (24h)"
          sub={(summary.disabled_keys || 0) + (summary.revoked_keys || 0) > 0
            ? `${summary.disabled_keys || 0} disabled · ${summary.revoked_keys || 0} revoked`
            : null}
        />
      </div>

      {/* Toolbar: search left · Filter → Refresh → Clear right (DS utilities only).
          The page-level Generate CTA lives in the parent header when
          showToolbarCreate is false, so this row never repeats it. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <DebouncedSearchInput
          value={searchQuery}
          onSearch={setSearchQuery}
          placeholder="Search keys or APIs (recruitment, phone, dorm…)"
          theme="admin"
          className="w-full sm:max-w-xl sm:flex-1"
        />
        <div className="flex items-center gap-2 flex-shrink-0">
          <FilterButton
            theme="admin"
            isOpen={showFilters}
            activeCount={statusFilter.length}
            onClick={() => setShowFilters((v) => !v)}
            className="flex-shrink-0"
          />
          <button
            type="button"
            onClick={() => refetch()}
            className="icon-button-admin flex-shrink-0"
            title="Refresh"
            aria-label="Refresh"
            disabled={isFetching || actionBusy}
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          {statusFilter.length > 0 && (
            <button
              type="button"
              onClick={() => setStatusFilter([])}
              className="icon-button-admin flex-shrink-0"
              title="Clear filters"
              aria-label="Clear filters"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          {showToolbarCreate && (
            <button
              type="button"
              onClick={() => openCreateModal({ search: searchQuery })}
              className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2"
              aria-label="Generate API Key"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Generate API Key</span>
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="flex items-center flex-wrap gap-2 mb-6 pt-1">
          <span className="text-xs font-medium text-gray-600 mr-1">Status:</span>
          {STATUS_FILTER_OPTIONS.map((opt) => {
            const active = statusFilter.includes(opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                aria-pressed={active}
                onClick={() => setStatusFilter((prev) =>
                  active ? prev.filter((s) => s !== opt.id) : [...prev, opt.id]
                )}
                className={`tab-btn-admin tab-btn-admin-sm ${active ? 'active' : ''}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}

      <AvailableApisCatalog
        scopes={scopes}
        categoryOrder={categoryOrder}
        searchQuery={searchQuery}
        onGenerate={(scope) => openCreateModal({ scopes: [scope.id] })}
      />

      <h3 className="text-sm font-semibold text-gray-900 mb-3">Issued keys</h3>

      {filteredKeys.length === 0 ? (
        <EmptyState
          onCreate={() => openCreateModal()}
          hasFilters={!!searchQuery || statusFilter.length > 0}
          catalogMatchCount={catalogMatchCount}
        />
      ) : (
        <div className="space-y-4">
          {filteredKeys.map((key) => (
            <ServiceKeyCard
              key={key.id}
              apiKey={key}
              expanded={!!expandedKeys[key.id]}
              onToggle={() => toggleExpand(key.id)}
              onToggleActive={() => handleToggleActive(key)}
              onEdit={() => setKeyToEdit(key)}
              onRotate={() => setKeyToRotate(key)}
              onRevoke={() => setKeyToRevoke(key)}
            />
          ))}
        </div>
      )}

      {showCreateModal && (
        <GenerateKeyModal
          scopes={scopes}
          categoryOrder={categoryOrder}
          apiBase={apiBase}
          showExcludedInfo={showExcludedInfo}
          initialSelectedScopes={createPrefill.scopes}
          initialScopeSearch={createPrefill.search}
          onClose={() => {
            setShowCreateModal(false);
            setCreatePrefill({ scopes: [], search: '' });
          }}
          onCreated={(data) => {
            setShowCreateModal(false);
            setCreatePrefill({ scopes: [], search: '' });
            setRevealedKey({ ...data, mode: 'created' });
            invalidateKeys();
          }}
        />
      )}

      {revealedKey && (
        <KeyRevealModal
          payload={revealedKey}
          onClose={() => setRevealedKey(null)}
        />
      )}

      {keyToEdit && (
        <EditKeyModal
          apiKey={keyToEdit}
          apiBase={apiBase}
          onClose={() => setKeyToEdit(null)}
          onSaved={() => {
            setKeyToEdit(null);
            invalidateKeys();
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!keyToRevoke}
        title="Revoke API key permanently?"
        confirmLabel="Revoke permanently"
        variant="danger"
        onConfirm={handleRevoke}
        onCancel={() => setKeyToRevoke(null)}
        theme="admin"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            This will permanently destroy the key for <strong>{keyToRevoke?.app_name}</strong>.
            The integration will stop working immediately and the key cannot be re-enabled —
            you'll need to generate a new one.
          </p>
          <p className="text-sm text-gray-500">
            Tip: if you just want to pause access temporarily, use the <strong>Disable</strong> toggle
            instead — that's reversible.
          </p>
          {keyToRevoke && (
            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 space-y-1">
              <div><span className="text-gray-500">Key prefix:</span> <code className="font-mono">{keyToRevoke.key_prefix}</code></div>
              <div><span className="text-gray-500">Scopes:</span> {keyToRevoke.allowed_scopes?.map(s => s.label).join(', ') || '—'}</div>
              <div><span className="text-gray-500">Created:</span> {formatDate(keyToRevoke.created_at)}</div>
            </div>
          )}
        </div>
      </ConfirmModal>

      <ConfirmModal
        isOpen={!!keyToRotate}
        title="Rotate API key?"
        confirmLabel="Rotate now"
        variant="primary"
        onConfirm={handleRotateConfirm}
        onCancel={() => setKeyToRotate(null)}
        theme="admin"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            We'll issue a new key for <strong>{keyToRotate?.app_name}</strong> with the
            same scopes and settings. The current key will continue to work for{' '}
            <strong>24 hours</strong> so the consumer can swap over without downtime.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
            <p className="font-medium mb-0.5">You'll see the new raw key only once.</p>
            <p>Make sure you have somewhere ready to paste it.</p>
          </div>
        </div>
      </ConfirmModal>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const MetricCard = ({ icon: Icon, iconBg, iconColor, value, label, sub }) => (
  <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
    <div className="flex items-center">
      <div className={`w-9 h-9 sm:w-12 sm:h-12 ${iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${iconColor}`} />
      </div>
      <div className="ml-2 sm:ml-4 min-w-0">
        <p className="text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-xs sm:text-sm text-gray-600 truncate">{label}</p>
        {sub && <p className="text-xs text-amber-600 mt-0.5 truncate">{sub}</p>}
      </div>
    </div>
  </div>
);

const EmptyState = ({ onCreate, hasFilters, catalogMatchCount = 0 }) => {
  if (hasFilters && catalogMatchCount > 0) {
    return (
      <div className="text-center py-8">
        <KeyRound className="w-10 h-10 text-gray-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No issued keys match</h3>
        <p className="text-sm text-gray-500 max-w-md mx-auto">
          A matching API is listed under Available APIs above. Generate a key to grant it to an integration.
        </p>
      </div>
    );
  }
  if (hasFilters) {
    return (
      <div className="text-center py-12">
        <KeyRound className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No keys match your filters</h3>
        <p className="text-sm text-gray-500">Try clearing search and filters above.</p>
      </div>
    );
  }
  return (
    <div className="text-center py-12">
      <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <KeyRound className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">No API keys yet</h3>
      <p className="text-sm text-gray-500 max-w-sm mx-auto">
        Generate a key to allow an external service to call HR Portal APIs with scoped access.
      </p>
      <button type="button" onClick={onCreate} className="btn btn-admin-primary mt-4">
        <Plus className="w-4 h-4" /> Generate Your First API Key
      </button>
    </div>
  );
};

const CatalogScopeRow = ({ scope, onGenerate }) => {
  const risk = RISK_CONFIG[scope.risk_level] || RISK_CONFIG.medium;
  const pending = scope.enforcement_status === 'pending';
  return (
    <li className="px-4 py-3 border-t border-gray-100 first:border-t-0">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center flex-wrap gap-2 mb-1">
            <span className="text-sm font-semibold text-gray-900">{scope.label}</span>
            <code className="text-xs font-mono text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              {scope.id}
            </code>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${risk.bg} ${risk.text} ${risk.border}`}>
              {risk.label}
            </span>
            {scope.compliance_tags?.map((tag) => (
              <span key={tag} className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                {tag}
              </span>
            ))}
            {pending && (
              <StatusBadge status="pending" label="Pending wiring" />
            )}
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">{scope.description}</p>
          {scope.endpoints?.length > 0 && (
            <div className="mt-2 space-y-1">
              {scope.endpoints.map((endpoint) => (
                <code
                  key={endpoint}
                  className="block text-xs font-mono text-gray-700 break-all"
                >
                  {endpoint}
                </code>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => onGenerate(scope)}
          className="btn btn-admin-primary shrink-0 w-11 h-11 min-h-0 max-h-11 p-0 gap-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-3 sm:py-2 sm:gap-2"
          aria-label={`Generate key for ${scope.label}`}
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Generate key</span>
        </button>
      </div>
    </li>
  );
};

const AvailableApisCatalog = ({ scopes, categoryOrder = [], searchQuery, onGenerate }) => {
  const [expanded, setExpanded] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(() => new Set(['Operations']));

  const searching = Boolean(searchQuery?.trim());
  const activeCategory = categoryFilter;
  const filtered = useMemo(() => {
    const matched = (scopes || []).filter((scope) => scopeMatchesQuery(scope, searchQuery));
    if (searching || !activeCategory) return matched;
    return matched.filter((scope) => scope.category === activeCategory);
  }, [scopes, searchQuery, searching, activeCategory]);
  const groups = useMemo(
    () => groupScopesByCategory(filtered, categoryOrder),
    [filtered, categoryOrder],
  );
  const chipCategories = useMemo(() => {
    if (categoryOrder?.length) {
      const present = new Set((scopes || []).map((scope) => scope.category));
      return categoryOrder.filter((cat) => present.has(cat));
    }
    return groupScopesByCategory(scopes || []).map((group) => group.category);
  }, [scopes, categoryOrder]);

  useEffect(() => {
    if (searching && filtered.length > 0) {
      setExpanded(true);
      setExpandedCategories(new Set(groups.map((group) => group.category)));
    }
  }, [searching, filtered.length, groups]);

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  if (!scopes?.length) return null;
  if (searching && filtered.length === 0) return null;

  return (
    <div className="mb-6">
      <AccordionSection
        theme="admin"
        expanded={expanded}
        onToggle={() => setExpanded((open) => !open)}
        contentVariant="flush"
        icon={<Layers className="w-4 h-4 text-red-600" />}
        iconBg="bg-red-50"
        title="Available APIs"
        trailing={
          <span className="text-xs text-gray-500">
            {filtered.length} {filtered.length === 1 ? 'resource' : 'resources'}
          </span>
        }
      >
        <div className="px-4 py-3 border-b border-gray-100 space-y-3">
          <p className="text-sm text-gray-600 leading-relaxed">
            These are the resources you can grant when generating a key. Issued keys appear below.
          </p>
          {!searching && (
            <CategoryChips
              categories={chipCategories}
              value={activeCategory}
              onChange={(next) => {
                setCategoryFilter(next);
                if (next) {
                  setExpandedCategories(new Set([next]));
                } else {
                  setExpandedCategories(new Set(chipCategories));
                }
              }}
            />
          )}
        </div>
        {groups.map((group) => {
          const categoryOpen = searching || expandedCategories.has(group.category) || Boolean(activeCategory);
          return (
            <div key={group.category} className="border-b border-gray-100 last:border-b-0">
              <button
                type="button"
                onClick={() => toggleCategory(group.category)}
                className="flex items-center gap-2 w-full px-4 pt-3 pb-2 text-left"
                aria-expanded={categoryOpen}
              >
                <ChevronDown
                  className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${categoryOpen ? '' : '-rotate-90'}`}
                />
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  {group.category}
                </h4>
                <span className="text-xs text-gray-400">·</span>
                <span className="text-xs text-gray-400">
                  {group.items.length} {group.items.length === 1 ? 'resource' : 'resources'}
                </span>
                <div className="flex-1 border-t border-gray-200 ml-1" />
              </button>
              {categoryOpen && (
                <ul>
                  {group.items.map((scope) => (
                    <CatalogScopeRow key={scope.id} scope={scope} onGenerate={onGenerate} />
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </AccordionSection>
    </div>
  );
};

const LoadingState = () => (
  <div className="animate-pulse">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full" />
            <div className="ml-2 sm:ml-4 flex-1">
              <div className="h-6 bg-gray-200 rounded w-16 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-24" />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="space-y-4">
      {[...Array(2)].map((_, i) => (
        <div key={i} className="h-32 bg-gray-200 rounded-2xl" />
      ))}
    </div>
  </div>
);

const ScopePill = ({ scope }) => {
  const risk = RISK_CONFIG[scope.risk_level] || RISK_CONFIG.medium;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${risk.bg} ${risk.text} ${risk.border}`}
      title={scope.description}
    >
      <Database className="w-3 h-3" />
      {scope.label}
    </span>
  );
};

const getAuthorisedEndpointGroups = (apiKey) => (
  (apiKey.allowed_scopes || [])
    .filter(scope => scope.endpoints?.length > 0)
    .map(scope => ({
      id: scope.id,
      label: scope.label || scope.id,
      enforcementStatus: scope.enforcement_status,
      endpoints: scope.endpoints,
    }))
);


// ---------------------------------------------------------------------------
// CopyIconButton — compact, icon-only "copy to clipboard" affordance.
//
// Used to copy non-secret identifiers (e.g. the key_prefix) straight from a
// key card without opening anything. Swaps to a green check for 2s on success
// and falls back to the manual-copy toast via copyToClipboard() when the
// async Clipboard API is unavailable (HTTP-over-LAN). Pass stopPropagation
// when rendered inside another clickable surface so the copy doesn't also
// trigger the parent (e.g. an accordion toggle).
// ---------------------------------------------------------------------------
const CopyIconButton = ({
  value,
  title = 'Copy',
  successMessage = 'Copied to clipboard',
  stopPropagation = false,
  className = '',
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    if (stopPropagation) {
      e.stopPropagation();
      e.preventDefault();
    }
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(true);
      toast.success(successMessage);
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Couldn't copy automatically — select the text and copy manually (Ctrl/Cmd+C).");
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={title}
      aria-label={title}
      className={`inline-flex items-center justify-center p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 transition-colors ${className}`}
    >
      {copied
        ? <CheckCircle className="w-3.5 h-3.5 text-green-600" />
        : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};

const ServiceKeyCard = ({ apiKey, expanded, onToggle, onToggleActive, onEdit, onRotate, onRevoke }) => {
  const cfg = STATUS_ICON[apiKey.status] || STATUS_ICON.disabled;
  const StatusIcon = cfg.icon;
  const isPermanentlyDead = apiKey.status === 'revoked' || apiKey.status === 'expired';
  const authorisedEndpointGroups = getAuthorisedEndpointGroups(apiKey);

  return (
    <AccordionSection
      expanded={expanded}
      onToggle={onToggle}
      contentVariant="padded"
      icon={<StatusIcon className={`w-4 h-4 ${cfg.text}`} />}
      iconBg={cfg.bg}
      title={
        <span className="flex items-center gap-2 min-w-0">
          <span className="font-semibold truncate">{apiKey.app_name}</span>
          <code className="hidden sm:inline flex-shrink-0 text-xs font-mono text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
            {apiKey.key_prefix}
          </code>
        </span>
      }
      trailing={<StatusBadge status={apiKey.status} showIcon />}
    >
      <div className="space-y-5">
        {/* Scopes */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
            Resources / Scopes
          </p>
          <div className="flex items-center flex-wrap gap-1.5">
            {apiKey.allowed_scopes?.length > 0
              ? apiKey.allowed_scopes.map(s => <ScopePill key={s.id} scope={s} />)
              : <span className="text-sm text-gray-400">No scopes</span>}
          </div>
        </div>

        {/* Description */}
        {apiKey.description && (
          <p className="text-sm text-gray-600 leading-relaxed">
            {apiKey.description}
          </p>
        )}
        <p className="text-xs text-gray-500">
          Scopes are immutable after creation — rotate or generate a new key to change resources.
        </p>

        {/* Key facts grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3 text-sm">
          <Fact label="Last Used" value={formatRelativeTime(apiKey.last_used_at)} />
          <Fact label="Requests (24h)" value={(apiKey.usage?.requests_24h || 0).toLocaleString()} />
          <Fact
            label="Errors (24h)"
            value={apiKey.usage?.errors_24h || 0}
            valueClass={apiKey.usage?.errors_24h > 0 ? 'text-red-600' : 'text-gray-900'}
          />
          <Fact
            label="Rate Limit"
            value={`${apiKey.rate_limit_per_minute}/min`}
          />
        </div>

        {/* Expiry bar */}
        {apiKey.expires_at && !isPermanentlyDead && (
          <div>
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-gray-500">
                {apiKey.status === 'disabled' ? 'Paused — would expire' : 'Expires'} {formatDate(apiKey.expires_at)}
              </span>
              {apiKey.days_remaining != null && (
                <span className="text-gray-500 font-medium">{apiKey.days_remaining}d remaining</span>
              )}
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${getExpiryBarColor(apiKey)}`}
                style={{ width: `${getExpiryBarPercent(apiKey)}%` }}
              />
            </div>
          </div>
        )}

        {!apiKey.expires_at && apiKey.is_active && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Clock className="w-3.5 h-3.5" />
            <span>Never expires — managed entirely via enable/disable</span>
          </div>
        )}

        {/* Expanded detail rows */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-5 border-t border-gray-200">
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Key className="w-4 h-4" />
              Key Details
            </h4>
            <dl className="space-y-2 text-sm">
              <DetailRow label="Key prefix" value={
                <span className="inline-flex items-center gap-1.5">
                  <code className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">{apiKey.key_prefix}</code>
                  <CopyIconButton
                    value={apiKey.key_prefix}
                    title="Copy key prefix"
                    successMessage="Key prefix copied"
                  />
                </span>
              } />
              <DetailRow label="Created" value={
                <>
                  {formatDateTime(apiKey.created_at)}
                  {apiKey.created_by_name && (
                    <span className="text-gray-400"> · by {apiKey.created_by_name}</span>
                  )}
                </>
              } />
              <DetailRow label="Expires" value={apiKey.expires_at ? formatDateTime(apiKey.expires_at) : 'Never'} />
              <DetailRow label="Last used" value={formatRelativeTime(apiKey.last_used_at)} />
            </dl>
            {!isPermanentlyDead && (
              <p className="mt-3 text-xs text-gray-500 leading-relaxed flex items-start gap-1.5">
                <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-gray-400" />
                <span>
                  Only the prefix above is stored — we keep a SHA-256 hash of the secret,
                  so the full key can't be shown again. Lost it?{' '}
                  {apiKey.is_active
                    ? <>Use <strong>Rotate key</strong> below to issue a fresh secret (24h grace on the old one).</>
                    : <>Re-enable the key, then rotate it to issue a fresh secret.</>}
                </span>
              </p>
            )}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Server className="w-4 h-4" />
              Endpoints &amp; Usage (30d)
            </h4>

            <div className="mb-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Authorised endpoints
                </p>
                <span className="text-xs text-gray-400">
                  {authorisedEndpointGroups.reduce((n, group) => n + group.endpoints.length, 0)} granted
                </span>
              </div>
              {authorisedEndpointGroups.length > 0 ? (
                <div className="divide-y divide-gray-100 max-h-56 overflow-y-auto border-t border-gray-100">
                  {authorisedEndpointGroups.map((group) => (
                    <div key={group.id} className="py-3">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="text-xs font-semibold text-gray-700">{group.label}</span>
                        {group.enforcementStatus === 'pending' && (
                          <StatusBadge status="pending" label="Pending wiring" />
                        )}
                      </div>
                      <div className="space-y-1">
                        {group.endpoints.map((endpoint) => (
                          <code
                            key={`${group.id}-${endpoint}`}
                            className="block text-xs font-mono text-gray-700 bg-gray-50 px-2 py-1 break-all"
                          >
                            {endpoint}
                          </code>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">
                  No endpoint metadata is registered for this key&apos;s selected resources.
                </p>
              )}
            </div>

            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Recent usage
            </p>
            {apiKey.endpoint_hits?.length > 0 ? (
              <div className="divide-y divide-gray-100 mb-3 border-t border-gray-100">
                {apiKey.endpoint_hits.slice(0, 8).map((hit, idx) => (
                  <div key={idx} className="py-2 flex items-center justify-between">
                    <code className="text-xs text-gray-700 font-mono truncate mr-3">{hit.endpoint}</code>
                    <span className="text-xs font-medium text-gray-500 whitespace-nowrap">
                      {hit.count?.toLocaleString() || 0} hits
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 mb-3">No traffic in the last 30 days.</p>
            )}
            <div className="grid grid-cols-3 gap-3">
              {[
                { window: '24h', label: '24 hours' },
                { window: '7d',  label: '7 days' },
                { window: '30d', label: '30 days' },
              ].map(({ window, label }) => (
                <div key={window} className="text-center py-2">
                  <p className="text-lg font-bold text-gray-900">
                    {(apiKey.usage?.[`requests_${window}`] || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">{label}</p>
                  {(apiKey.usage?.[`errors_${window}`] || 0) > 0 && (
                    <p className="text-xs text-red-600">{apiKey.usage[`errors_${window}`]} err</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div className="flex items-center flex-wrap gap-2 pt-4 border-t border-gray-200">
          {!isPermanentlyDead && (
            <button
              type="button"
              onClick={onToggleActive}
              className={apiKey.is_active ? 'btn btn-sm btn-admin-outline' : 'btn btn-sm btn-admin-primary'}
            >
              <Power className="w-4 h-4 mr-1.5" />
              {apiKey.is_active ? 'Disable' : 'Re-enable'}
            </button>
          )}
          {!isPermanentlyDead && (
            <button type="button" onClick={onEdit} className="btn btn-sm btn-admin-outline">
              <Pencil className="w-4 h-4 mr-1.5" />
              Edit
            </button>
          )}
          {apiKey.is_active && (
            <button type="button" onClick={onRotate} className="btn btn-sm btn-admin-outline">
              <RefreshCw className="w-4 h-4 mr-1.5" />
              Rotate key
            </button>
          )}
          {!isPermanentlyDead && (
            <button type="button" onClick={onRevoke} className="btn btn-sm btn-danger ml-auto">
              <Trash2 className="w-4 h-4 mr-1.5" />
              Revoke permanently
            </button>
          )}
        </div>
      </div>
    </AccordionSection>
  );
};

const Fact = ({ label, value, valueClass = 'text-gray-900' }) => (
  <div>
    <span className="text-gray-500 text-xs uppercase tracking-wider">{label}</span>
    <p className={`font-medium ${valueClass}`}>{value}</p>
  </div>
);

const DetailRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3">
    <dt className="text-gray-500 whitespace-nowrap">{label}</dt>
    <dd className="text-gray-900 text-right">{value}</dd>
  </div>
);

// ---------------------------------------------------------------------------
// Generate Key Modal
// ---------------------------------------------------------------------------

const GenerateKeyModal = ({
  scopes,
  categoryOrder = [],
  onClose,
  onCreated,
  apiBase = '/service-api-keys',
  showExcludedInfo = true,
  initialSelectedScopes = [],
  initialScopeSearch = '',
}) => {
  const [creating, setCreating] = useState(false);
  const [appName, setAppName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState(initialSelectedScopes);
  const [description, setDescription] = useState('');
  const [neverExpires, setNeverExpires] = useState(false);
  const [expiresAt, setExpiresAt] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d;
  });
  const [rateLimit, setRateLimit] = useState(60);
  const [scopeSearch, setScopeSearch] = useState(initialScopeSearch);
  const [categoryFilter, setCategoryFilter] = useState(() => {
    if (!initialSelectedScopes?.length) return '';
    const match = (scopes || []).find((scope) => initialSelectedScopes.includes(scope.id));
    return match?.category || '';
  });

  const toggleScope = (id) => {
    setSelectedScopes(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const searchingScopes = Boolean(scopeSearch.trim());
  const filteredScopes = useMemo(
    () => (scopes || []).filter((scope) => {
      if (!scopeMatchesQuery(scope, scopeSearch)) return false;
      if (!searchingScopes && categoryFilter && scope.category !== categoryFilter) {
        return false;
      }
      return true;
    }),
    [scopes, scopeSearch, searchingScopes, categoryFilter],
  );
  const chipCategories = useMemo(() => {
    if (categoryOrder?.length) {
      const present = new Set((scopes || []).map((scope) => scope.category));
      return categoryOrder.filter((cat) => present.has(cat));
    }
    return groupScopesByCategory(scopes || []).map((group) => group.category);
  }, [scopes, categoryOrder]);

  const canSubmit = appName.trim() && selectedScopes.length > 0 && rateLimit >= 1;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setCreating(true);
      const payload = {
        app_name: appName.trim(),
        scopes: selectedScopes,
        description: description.trim(),
        rate_limit_per_minute: parseInt(rateLimit, 10),
        never_expires: neverExpires,
        expires_at: neverExpires ? null : (expiresAt ? expiresAt.toISOString() : null),
      };
      const response = await api.post(`${apiBase}/create/`, payload);
      toast.success('API key generated');
      onCreated(response.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate API key');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container modal-container-lg" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">
              <KeyRound className="w-5 h-5 inline mr-2 text-red-600" />
              Generate API Key
            </h3>
            <button onClick={onClose} className="modal-close-admin" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="modal-content">
              <div className="space-y-6">
                {/* App name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    App name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={appName}
                    onChange={(e) => setAppName(e.target.value)}
                    placeholder="e.g., Phone Directory Integration"
                    className="input-admin w-full"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Shown on the dashboard, in audit logs, and on every call this key makes.
                  </p>
                </div>

                {/* Scopes — grouped by category, with field-first search */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Resources <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Pick the API resources this key can access. The key is rejected (403)
                    when called against any endpoint not covered by these scopes.
                  </p>

                  <div className="mb-3 flex items-center gap-2">
                    <DebouncedSearchInput
                      value={scopeSearch}
                      onSearch={setScopeSearch}
                      placeholder="Search by field, resource, or keyword (e.g. recruitment, phone, dorm)..."
                      theme="admin"
                      debounceMs={150}
                    />
                  </div>
                  {!scopeSearch.trim() && (
                    <div className="mb-3">
                      <CategoryChips
                        categories={chipCategories}
                        value={categoryFilter}
                        onChange={setCategoryFilter}
                      />
                    </div>
                  )}
                  {scopeSearch.trim() && filteredScopes.length > 0 && (
                    <p className="text-xs text-gray-500 mb-2">
                      Showing <strong>{filteredScopes.length}</strong> of {scopes?.length || 0} Resources matching <span className="font-mono text-gray-700">"{scopeSearch}"</span>
                    </p>
                  )}

                  <ScopePicker
                    scopes={filteredScopes}
                    categoryOrder={categoryOrder}
                    selected={selectedScopes}
                    onToggle={toggleScope}
                    searchQuery={scopeSearch}
                    showExcludedInfo={showExcludedInfo}
                  />

                  {showExcludedInfo && <ExcludedFieldsPanel />}

                  {selectedScopes.length > 0 && (
                    <p className="text-xs text-gray-600 mt-2 flex items-center gap-1">
                      <Layers className="w-3.5 h-3.5" />
                      {selectedScopes.length} scope{selectedScopes.length > 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>

                {/* Expiry */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Expiration
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer p-3 rounded-xl border border-gray-200 hover:border-red-300 transition-colors">
                      <input
                        type="radio"
                        checked={!neverExpires}
                        onChange={() => setNeverExpires(false)}
                        className="accent-red-600 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm flex-1">Expires on a specific date</span>
                      {!neverExpires && (
                        <div className="w-44">
                          <DatePickerField
                            theme="admin"
                            selected={expiresAt}
                            onChange={(date) => setExpiresAt(date)}
                            minDate={new Date()}
                          />
                        </div>
                      )}
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-3 rounded-xl border border-gray-200 hover:border-red-300 transition-colors">
                      <input
                        type="radio"
                        checked={neverExpires}
                        onChange={() => setNeverExpires(true)}
                        className="accent-red-600 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm flex-1">Never expires (managed by enable/disable)</span>
                    </label>
                  </div>
                </div>

                {/* Rate limit */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Rate limit
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="1"
                      max="600"
                      value={rateLimit}
                      onChange={(e) => setRateLimit(Math.min(600, Math.max(1, parseInt(e.target.value, 10) || 1)))}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="input-admin w-28"
                    />
                    <span className="text-sm text-gray-500">requests / minute (max 600)</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Lower this for sensitive scopes. Exceeding the limit returns HTTP 429.
                  </p>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Description <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="e.g., Ops Bot — looks up worker mobile numbers when a site supervisor pages them."
                    rows="2"
                    className="textarea-admin w-full"
                  />
                </div>

                {/* Security notice */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium mb-1">The raw key is shown only once.</p>
                      <p>
                        We store a SHA-256 hash, not the key itself — we cannot retrieve
                        or display it again. Copy it into your secret store immediately.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" onClick={onClose} className="btn btn-admin-outline">
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit || creating}
                className="btn btn-admin-primary"
              >
                {creating ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><KeyRound className="w-4 h-4 mr-2" /> Generate Key</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Portal>
  );
};

// ---------------------------------------------------------------------------
// FieldChip — one parsed entry from data_access rendered as a compact pill.
// If the entry had a parenthetical qualifier (e.g. "leave_type (e.g. AL, OIL,
// MC — NOT the underlying medical reason)") the qualifier becomes a hover
// tooltip + an Info dot, so the chip stays short but the nuance is one hover
// away.  Useful for fields like `leave_type` where the qualifier carries the
// "but we DON'T expose X" subtlety.
// ---------------------------------------------------------------------------
const FieldChip = ({ name, qualifier }) => (
  <span
    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 hover:bg-gray-200 text-[11px] font-mono text-gray-700 border border-gray-200 transition-colors"
    title={qualifier || undefined}
  >
    {name}
    {qualifier && <Info className="w-2.5 h-2.5 text-gray-400" />}
  </span>
);

// ---------------------------------------------------------------------------
// ScopePickerEmptyState — shown when the search box yields zero scope matches.
// If the typed term hits an EXCLUDED domain keyword (e.g. "nric", "salary"),
// explain why it is not pickable and point to the HR / DPO process.
// ---------------------------------------------------------------------------
const ScopePickerEmptyState = ({ searchQuery, showExcludedInfo = true }) => {
  const excludedHit = useMemo(
    () => (showExcludedInfo ? findExcludedHit(searchQuery) : null),
    [searchQuery, showExcludedInfo]
  );
  if (excludedHit) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              &quot;{searchQuery}&quot; is part of <span className="underline">{excludedHit.label}</span> — not exposable via API Key
            </p>
            <p className="text-sm text-amber-800 mt-1 leading-relaxed">{excludedHit.reason}</p>
            <p className="text-xs text-amber-700 mt-2">
              Examples in this domain:{' '}
              {excludedHit.examples.slice(0, 4).join(', ')}
              {excludedHit.examples.length > 4 ? '…' : ''}
            </p>
            <p className="text-sm text-amber-900 mt-3 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>
                These domains require written DPO sign-off and a registry change —
                contact HR / the Data Protection Officer. They are not available
                via long-lived Service API Keys.
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  }
  if (searchQuery) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-gray-600">
          No Resources match <span className="font-mono text-gray-800">&quot;{searchQuery}&quot;</span>.
        </p>
        {showExcludedInfo && (
          <p className="text-xs text-gray-500 mt-1">
            Try a different term, or check the panel below for deliberately excluded domains.
          </p>
        )}
      </div>
    );
  }
  return <p className="text-sm text-gray-400 italic">No Resources available.</p>;
};

// ---------------------------------------------------------------------------
// ExcludedFieldsPanel — collapsible "Looking for NRIC, DOB, salary, MC?"
// Keep IN SYNC with the EXCLUDED list in staff_core/service_auth.py's
// SERVICE_SCOPE_REGISTRY docstring.
// ---------------------------------------------------------------------------
const ExcludedFieldsPanel = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4">
      <AccordionSection
        expanded={open}
        onToggle={() => setOpen((o) => !o)}
        contentVariant="padded"
        icon={<Lock className="w-4 h-4 text-gray-500" />}
        iconBg="bg-gray-100"
        title={
          <span className="text-sm font-semibold text-gray-800">
            Looking for NRIC, DOB, salary, MC, bank account…?
          </span>
        }
      >
        <div className="text-xs text-gray-600 mb-3 leading-relaxed">
          Service API Keys are for long-lived machine-to-machine integrations.
          Domains on this list are either statutorily confidential, high-blast-radius
          PII, or third-party personal data. They are not available via Service API Keys.
          Request access through HR / the Data Protection Officer (written DPO sign-off
          and a registry change are required).
        </div>

        <ul className="space-y-2.5">
          {EXCLUDED_DOMAINS.map((domain) => (
            <li key={domain.id} className="py-2 border-t border-gray-100 first:border-t-0 first:pt-0">
              <div className="flex items-start gap-2">
                <Lock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-800">{domain.label}</p>
                  <p className="text-xs text-gray-600 mt-0.5">{domain.reason}</p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {domain.examples.map((ex) => (
                      <span
                        key={ex}
                        className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-[11px] font-mono text-gray-600"
                      >
                        {ex}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </AccordionSection>
    </div>
  );
};

const ScopePicker = ({
  scopes,
  categoryOrder = [],
  selected,
  onToggle,
  searchQuery = '',
  showExcludedInfo = true,
}) => {
  if (!scopes || scopes.length === 0) {
    return <ScopePickerEmptyState searchQuery={searchQuery} showExcludedInfo={showExcludedInfo} />;
  }

  const groups = groupScopesByCategory(scopes, categoryOrder);

  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <div key={group.category}>
          <div className="flex items-center gap-2 mb-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              {group.category}
            </h4>
            <span className="text-xs text-gray-400">·</span>
            <span className="text-xs text-gray-400">{group.items.length} {group.items.length === 1 ? 'resource' : 'resources'}</span>
            <div className="flex-1 border-t border-gray-200 ml-1"></div>
          </div>
          <div className="space-y-2">
            {group.items.map((scope) => (
              <ScopeOptionCard
                key={scope.id}
                scope={scope}
                selected={selected.includes(scope.id)}
                onToggle={() => onToggle(scope.id)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const ScopeOptionCard = ({ scope, selected, onToggle }) => {
  const risk = RISK_CONFIG[scope.risk_level] || RISK_CONFIG.medium;
  const [showEndpoints, setShowEndpoints] = useState(false);
  const isPending = scope.enforcement_status === 'pending';

  return (
    <div
      className={`rounded-xl border-2 transition-all ${
        selected ? 'border-red-500 bg-red-50/40' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={selected}
        onClick={onToggle}
        className="w-full text-left p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded-xl"
      >
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className={`mt-1 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
              selected ? 'bg-red-600 border-red-600' : 'bg-white border-gray-300'
            }`}
          >
            {selected && <CheckCircle className="w-3 h-3 text-white" />}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-2 mb-1">
              <span className="font-semibold text-gray-900">{scope.label}</span>
              <code className="text-xs font-mono text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                {scope.id}
              </code>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${risk.bg} ${risk.text} ${risk.border}`}>
                {risk.label}
              </span>
              {scope.compliance_tags?.map(tag => (
                <span key={tag} className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                  {tag}
                </span>
              ))}
              {isPending && (
                <StatusBadge status="pending" label="Pending wiring" />
              )}
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">{scope.description}</p>
            {scope.data_access && (
              <div className="mt-2">
                <p className="text-xs font-medium text-gray-600 mb-1">Fields returned</p>
                <div className="flex flex-wrap gap-1">
                  {splitDataAccess(scope.data_access).map((field, idx) => (
                    <FieldChip key={`${field.name}-${idx}`} name={field.name} qualifier={field.qualifier} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </button>
      {scope.endpoints?.length > 0 && (
        <div className="px-4 pb-3">
          <button
            type="button"
            onClick={() => setShowEndpoints((v) => !v)}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showEndpoints ? 'rotate-180' : ''}`} />
            {showEndpoints ? 'Hide' : 'Show'} {scope.endpoints.length} endpoint{scope.endpoints.length > 1 ? 's' : ''}
          </button>
          {showEndpoints && (
            <div className="mt-2 divide-y divide-gray-100 border-t border-gray-100">
              {scope.endpoints.map((ep, idx) => (
                <code key={idx} className="block py-2 text-xs font-mono text-gray-700">
                  {ep}
                </code>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Key Reveal Modal — shown after create or rotate
// ---------------------------------------------------------------------------

const KeyRevealModal = ({ payload, onClose }) => {
  const [copiedField, setCopiedField] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const copy = async (text, field) => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopiedField(field);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedField(null), 2000);
    } else {
      toast.error('Couldn\'t copy automatically — select the text and copy manually (Ctrl/Cmd+C).');
    }
  };

  const isRotated = payload.mode === 'rotated';
  const apiOrigin = getPublicApiOrigin();

  const curlSnippet = useMemo(() => (
    `curl -X GET "${apiOrigin}/api/v1/<endpoint>" \\\n  -H "X-Api-Key: ${payload.api_key}" \\\n  -H "Accept: application/json"`
  ), [apiOrigin, payload.api_key]);

  return (
    <Portal>
      <div className="modal-overlay">
        <div className="modal-container modal-container-md" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">
              <CheckCircle className="w-5 h-5 text-green-600 inline mr-2" />
              {isRotated ? 'Key rotated' : 'API key generated'}
            </h3>
            <button onClick={onClose} className="modal-close-admin" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="modal-content">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">
                  <p className="font-semibold mb-1">Copy the key now.</p>
                  <p>
                    HR Portal only stores a SHA-256 hash; this is the <strong>only</strong> moment
                    the raw key is available. Closing this dialog without copying means generating a new one.
                  </p>
                </div>
              </div>
            </div>

            {isRotated && payload.old_key_valid_until && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 text-sm text-amber-900">
                <p className="flex items-center gap-2 font-medium mb-0.5">
                  <Clock className="w-4 h-4" />
                  Old key valid until {formatDateTime(payload.old_key_valid_until)}
                </p>
                <p className="text-amber-700">
                  Swap the consumer to the new key within {payload.grace_hours || 24} hours
                  to avoid downtime.
                </p>
              </div>
            )}

            <div className="space-y-4">
              <div className="text-sm">
                <div className="flex items-center gap-2 text-gray-700 mb-1.5">
                  <Zap className="w-4 h-4 text-red-600" />
                  <span><strong>{payload.app_name}</strong></span>
                </div>
                <div className="flex items-center flex-wrap gap-1.5 mb-1">
                  {payload.allowed_scopes?.map(s => <ScopePill key={s.id} scope={s} />)}
                </div>
                <p className="text-xs text-gray-500">
                  {payload.never_expires ? 'Never expires' : `Expires on ${formatDate(payload.expires_at)}`}
                  {' · '}
                  {payload.rate_limit_per_minute}/min rate limit
                </p>
              </div>

              <SecretBlock
                label="X-Api-Key header value"
                value={payload.api_key}
                copied={copiedField === 'api_key'}
                onCopy={() => copy(payload.api_key, 'api_key')}
              />

              <details className="bg-gray-50 rounded-xl border border-gray-200">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 px-4 py-3 select-none">
                  Quick-test snippet (curl)
                </summary>
                <div className="px-4 pb-4">
                  <SecretBlock
                    label=""
                    value={curlSnippet}
                    copied={copiedField === 'curl'}
                    onCopy={() => copy(curlSnippet, 'curl')}
                  />
                </div>
              </details>
            </div>

            <label className="flex items-start gap-2 mt-5 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-red-600 text-red-600 focus:ring-red-500"
              />
              <span>I&apos;ve copied the key to a secure location.</span>
            </label>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              disabled={!acknowledged}
              className="btn btn-admin-primary w-full sm:w-auto"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

const SecretBlock = ({ label, value, onCopy, copied }) => (
  <div>
    {(label || onCopy) && (
      <div className="flex items-center justify-between mb-1.5">
        {label && <label className="text-sm font-medium text-gray-700">{label}</label>}
        {onCopy && (
          <button type="button" onClick={onCopy} className="btn btn-sm btn-admin-outline ml-auto">
            {copied
              ? <><CheckCircle className="w-3.5 h-3.5 mr-1 text-green-600" /> Copied</>
              : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
          </button>
        )}
      </div>
    )}
    <div className="bg-gray-900 rounded-lg p-3 overflow-x-auto">
      <code className="text-xs text-green-400 break-all select-all whitespace-pre">{value}</code>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// EditKeyModal — description + rate limit only (scopes are immutable).
// ---------------------------------------------------------------------------
const EditKeyModal = ({ apiKey, apiBase, onClose, onSaved }) => {
  const [description, setDescription] = useState(apiKey.description || '');
  const [rateLimit, setRateLimit] = useState(String(apiKey.rate_limit_per_minute || 60));
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsed = parseInt(rateLimit, 10);
    if (!parsed || parsed < 1 || parsed > 600) {
      toast.error('Rate limit must be between 1 and 600');
      return;
    }
    try {
      setSaving(true);
      await api.patch(`${apiBase}/${apiKey.id}/`, {
        description: description.trim(),
        rate_limit_per_minute: parsed,
      });
      toast.success('Key updated');
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update key');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container modal-container-md" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">
              <Pencil className="w-5 h-5 inline mr-2 text-red-600" />
              Edit API Key
            </h3>
            <button type="button" onClick={onClose} className="modal-close-admin" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="modal-content space-y-5">
              <div className="text-sm text-gray-600">
                Editing <strong>{apiKey.app_name}</strong>
                <code className="ml-2 text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                  {apiKey.key_prefix}
                </code>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900 flex items-start gap-2">
                <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Scopes and app name are immutable. To change resources, rotate or generate a new key.
                </span>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows="3"
                  className="textarea-admin w-full"
                  placeholder="What this integration is for…"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Rate limit
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    max="600"
                    value={rateLimit}
                    onChange={(e) => setRateLimit(e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="input-admin w-28"
                  />
                  <span className="text-sm text-gray-500">requests / minute (max 600)</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" onClick={onClose} className="btn btn-admin-outline">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="btn btn-admin-primary">
                {saving ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                ) : (
                  'Save changes'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Portal>
  );
};

export default ServiceApiKeysTab;
